import { PaymentMethodEnum } from "../models/types.mjs";
import { SubscriptionStatus } from "../models/subscription.mjs";
import { extractPaymentMethodInputSnapshot } from "./snapshot.mjs";
import { getSubscriptionStatusFromSponsorInfo, normalizeSubscriptionStatus } from "./subscriptionStatus.mjs";
import { SPONSOR_TRANSITION_WINDOW_DAYS } from "./constants.mjs";
import { DateTime } from "luxon";

// src/paymentMethod/sponsored.ts
function paymentMethodSponsoredFromSnapshot(snap, now) {
	const trialStartDate = snap.trialStartDate ?? null;
	const trialEndDate = snap.trialEndDate ?? null;
	const trialStatus = trialEndDate ? DateTime.fromJSDate(trialEndDate).endOf("day").diff(now, "days").days >= 0 ? "active" : "expired" : "none";
	const trialActive = trialStatus === "active";
	const daysUntilTrialEnd = trialEndDate ? Math.ceil(DateTime.fromJSDate(trialEndDate).endOf("day").diff(now.startOf("day"), "days").days) : null;
	const sponsorEndDate = snap.sponsorInfo?.endDate ?? null;
	const daysUntilSponsorEnd = sponsorEndDate ? Math.ceil(DateTime.fromJSDate(sponsorEndDate, { zone: now.zone }).startOf("day").diff(now.startOf("day"), "days").days) : null;
	const isInSponsorTransitionWindow = daysUntilSponsorEnd !== null && daysUntilSponsorEnd >= 0 && daysUntilSponsorEnd <= 14;
	const subscriptionStatusFromCustomer = snap.signupPaymentEndDate ? DateTime.fromJSDate(snap.signupPaymentEndDate).diff(now, "days").days > -1 ? "active" : "expired" : "inactive";
	const subscriptionStatusFromTrial = trialActive ? "active" : "inactive";
	const subscriptionStatusFromSubscription = snap.hasSubscription ? normalizeSubscriptionStatus(snap.subscriptionStatus) : "inactive";
	const subscriptionStatusFromSponsorInfo = getSubscriptionStatusFromSponsorInfo(snap.sponsorInfo, snap.signupFlowCompleted, now);
	const subscriptionStatuses = [
		subscriptionStatusFromTrial,
		subscriptionStatusFromSubscription,
		subscriptionStatusFromCustomer,
		subscriptionStatusFromSponsorInfo
	];
	const subscriptionStatus = subscriptionStatuses.includes("active") ? "active" : subscriptionStatuses.includes("expired") ? "expired" : "inactive";
	const subscriptionActive = subscriptionStatus === "active";
	const subscriptionFrom = subscriptionStatusFromSubscription === "active" ? "subscription" : subscriptionStatusFromCustomer === "active" ? "customer" : subscriptionStatusFromSponsorInfo === "active" ? "sponsorInfo" : "none";
	const consultationIncluded = snap.signupFlowCompleted ? subscriptionStatusFromSponsorInfo === "active" && snap.sponsorInfo?.consultationIncluded || subscriptionStatusFromSubscription === "active" && snap.stripeProductConsultation || false : snap.sponsorInfo?.consultationIncluded ?? false;
	const subscriptionPlanType = {
		subscription: "subscription",
		customer: "subscription",
		sponsorInfo: "sponsored",
		none: null
	}[subscriptionFrom];
	const subscriptionPlanNames = {
		subscription: snap.productName || "Not Selected",
		customer: snap.productName || "Not Selected",
		sponsorInfo: snap.sponsorInfo ? consultationIncluded ? "Greatly Complete Care (Sponsored)" : "Greatly Classes Only (Sponsored)" : null,
		none: null
	};
	const placeholderForSubscriptionInfo = snap.sponsorInfo ? {
		planName: subscriptionPlanNames.sponsorInfo,
		lastPeriodEnd: snap.sponsorInfo.endDate,
		lastPeriodStart: snap.sponsorInfo.startDate,
		automaticallyRenewing: false,
		sponsorSubscriptionStatus: subscriptionStatusFromSponsorInfo
	} : null;
	const subscriptionScheduled = ["activated", "pending"].includes(snap.subscriptionScheduleStatus ?? "");
	const reviewCue = !subscriptionActive && !trialActive || trialActive && !subscriptionScheduled || isInSponsorTransitionWindow && !subscriptionScheduled || snap.subscriptionStatus === "past_due";
	return {
		showPlanSelectionAndStripeCheckout: false,
		neverSubscribedAndNoTrial: !snap.hasSignupPayment && !snap.hasSubscription && !snap.sponsorInfo && !trialActive,
		paymentCompleted: true,
		paymentNeeded: false,
		reviewCue,
		subscriptionStatus,
		subscriptionActive,
		subscriptionFrom,
		subscriptionPlanName: subscriptionPlanNames[subscriptionFrom],
		subscriptionPlanNames,
		subscriptionPlanType,
		placeholderForSubscriptionInfo,
		consultationIncluded,
		sponsorEndDate,
		daysUntilSponsorEnd,
		isInSponsorTransitionWindow,
		subscriptionScheduleStatus: snap.subscriptionScheduleStatus ?? null,
		trialStatus,
		trialStartDate,
		trialEndDate,
		trialActive,
		daysUntilTrialEnd
	};
}
const buildPaymentMethodSponsoredParams = (customer, trialInfo, subscription, sponsorInfo, product, subscriptionSchedule) => {
	return paymentMethodSponsoredFromSnapshot(extractPaymentMethodInputSnapshot({
		paymentMethodKind: "sponsored",
		signupPaymentInfo: customer?.signupPaymentInfo ?? null,
		signupFlowCompleted: customer?.signupFlowCompleted ?? false,
		trialInfo,
		subscription,
		sponsorInfo,
		product,
		subscriptionSchedule
	}), DateTime.now());
};

export { buildPaymentMethodSponsoredParams, paymentMethodSponsoredFromSnapshot };
//# sourceMappingURL=sponsored.mjs.map